import { Controller } from "@hotwired/stimulus"

// Two-step commodity/variety typeahead for the planting form.
//
// Step 1 — Crop field: searches commodities and varieties simultaneously.
//   Selecting a commodity-only result reveals the variety step.
//   Selecting a "Commodity: Variety" result fills both fields and skips step 2.
//
// Step 2 — Variety field: searches varieties scoped to the selected commodity.
//   Optional — grower can leave it blank.
//
// Targets:
//   input              — step 1 visible text input
//   results            — step 1 dropdown
//   varietySection     — wrapper div for step 2 (hidden until commodity selected)
//   varietyInput       — step 2 visible text input
//   varietyResults     — step 2 dropdown
//   commodityId        — hidden field for planting[commodity_id]
//   commodityVarietyId — hidden field for planting[commodity_variety_id]
//   dialog             — <dialog> for the add-variety modal
//
// Values:
//   url                      — search endpoint (/commodity_varieties/search)
//   newUrl                   — base URL for new variety form
//   initialCommodityId       — pre-selected commodity (edit form)
//   initialVarietyId         — pre-selected variety (edit form)

export default class extends Controller {
  static targets = [
    "input", "results",
    "varietySection", "varietyInput", "varietyResults",
    "commodityId", "commodityVarietyId",
    "addVarietyLink",
    "dialog"
  ]
  static values = {
    url: String,
    newUrl: String,
    initialCommodityId: Number,
    initialVarietyId: Number
  }

  connect() {
    this._results = []
    this._activeIndex = -1
    this._varietyResults = []
    this._varietyActiveIndex = -1
    this._selectedCommodityId = null

    this._onInput = this._onInput.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    this._onVarietyInput = this._onVarietyInput.bind(this)
    this._onVarietyKeydown = this._onVarietyKeydown.bind(this)
    this._onBlur = this._onBlur.bind(this)
    this._onFrameLoad = this._onFrameLoad.bind(this)

    this.inputTarget.addEventListener("input", this._onInput)
    this.inputTarget.addEventListener("keydown", this._onKeydown)

    if (this.hasVarietyInputTarget) {
      this.varietyInputTarget.addEventListener("input", this._onVarietyInput)
      this.varietyInputTarget.addEventListener("keydown", this._onVarietyKeydown)
    }

    document.addEventListener("click", this._onBlur)

    if (this.hasDialogTarget) {
      const frame = this.dialogTarget.querySelector("turbo-frame")
      if (frame) frame.addEventListener("turbo:frame-load", this._onFrameLoad)
    }

    // Re-initialise from server-rendered values (edit form)
    if (this.initialCommodityIdValue) {
      this._selectedCommodityId = this.initialCommodityIdValue
    }
  }

  disconnect() {
    this.inputTarget.removeEventListener("input", this._onInput)
    this.inputTarget.removeEventListener("keydown", this._onKeydown)

    if (this.hasVarietyInputTarget) {
      this.varietyInputTarget.removeEventListener("input", this._onVarietyInput)
      this.varietyInputTarget.removeEventListener("keydown", this._onVarietyKeydown)
    }

    document.removeEventListener("click", this._onBlur)

    if (this.hasDialogTarget) {
      const frame = this.dialogTarget.querySelector("turbo-frame")
      if (frame) frame.removeEventListener("turbo:frame-load", this._onFrameLoad)
    }
  }

  // ── Modal ──────────────────────────────────────────────────────────────────

  closeModal() {
    if (this.hasDialogTarget) {
      this.dialogTarget.close()
      const frame = this.dialogTarget.querySelector("turbo-frame")
      if (frame) frame.innerHTML = ""
    }
    this.inputTarget.focus()
  }

  backdropClick(event) {
    const box = this.dialogTarget.querySelector(".modal-box")
    if (box && !box.contains(event.target)) this.closeModal()
  }

  // ── Step 1 — Crop field ────────────────────────────────────────────────────

  async _onInput() {
    const q = this.inputTarget.value.trim()

    // Clear selections and hide variety step when crop field changes
    this.commodityIdTarget.value = ""
    this.commodityVarietyIdTarget.value = ""
    this._selectedCommodityId = null
    this._activeIndex = -1
    this._hideVarietySection()
    this._hideAddVarietyLink()

    if (q.length < 2) {
      this._hideResults()
      return
    }

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", q)
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    this._results = await response.json()
    this._renderResults(this._results, q)
  }

_onKeydown(e) {
    if (e.key === "Escape" && this.hasDialogTarget && this.dialogTarget.open) {
      this.closeModal()
      return
    }

    const items = this.resultsTarget.querySelectorAll("li")
    if (!items.length) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      this._setActive(Math.min(this._activeIndex + 1, items.length - 1), items)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      this._setActive(Math.max(this._activeIndex - 1, 0), items)
    } else if (e.key === "Enter") {
      e.preventDefault()
      const active = items[this._activeIndex]
      if (active) {
        if (active.dataset.openModal) {
          this._openModal(active.dataset.query)
        } else if (this._results[this._activeIndex]) {
          this._select(this._results[this._activeIndex])
        }
      }
    } else if (e.key === "Escape") {
      this._hideResults()
    }
  }

  _renderResults(results, q) {
    this.resultsTarget.innerHTML = ""
    this._activeIndex = -1

    if (results.length === 0) {
      const li = document.createElement("li")
      li.classList.add("px-3", "py-2", "text-sm", "cursor-pointer", "hover:bg-base-200")
      li.dataset.openModal = "true"
      li.dataset.query = q
      li.innerHTML = `No results for <strong>${this._escape(q)}</strong> &mdash; <span class="link">add as a new variety</span>`
      li.addEventListener("mousedown", (e) => { e.preventDefault(); this._openModal(q) })
      this.resultsTarget.appendChild(li)
      this.resultsTarget.classList.remove("hidden")
      return
    }

    results.forEach((result, index) => {
      const li = document.createElement("li")
      li.classList.add("px-3", "py-2", "cursor-pointer", "hover:bg-base-200", "text-sm")

      if (!result.commodity_variety_id && result.has_varieties) {
        // Commodity-only with varieties — hint that a variety can be selected
        li.innerHTML = `${this._escape(result.label)} <span class="opacity-40 text-xs ml-1">+ variety</span>`
      } else {
        li.textContent = result.label
      }

      li.addEventListener("mousedown", (e) => { e.preventDefault(); this._select(result) })
      li.addEventListener("mousemove", () => {
        const items = this.resultsTarget.querySelectorAll("li")
        this._setActive(index, items)
      })
      this.resultsTarget.appendChild(li)
    })

    this.resultsTarget.classList.remove("hidden")
  }

  _select(result) {
    this.commodityIdTarget.value = result.commodity_id
    this.commodityVarietyIdTarget.value = result.commodity_variety_id || ""
    this._results = []
    this._activeIndex = -1
    this._hideResults()

    if (result.commodity_variety_id) {
      // Full variety selection — show as "Commodity: Variety" and skip step 2
      this.inputTarget.value = result.label
      this._hideVarietySection()
      this._hideAddVarietyLink()
    } else if (result.has_varieties) {
      // Commodity-only with varieties — reveal variety step
      this.inputTarget.value = result.label
      this._selectedCommodityId = result.commodity_id
      this._hideAddVarietyLink()
      this._showVarietySection()
    } else {
      // Commodity with no varieties — skip variety step, offer link to add one
      this.inputTarget.value = result.label
      this._selectedCommodityId = result.commodity_id
      this._hideVarietySection()
      this._showAddVarietyLink()
    }
  }

  // ── Step 2 — Variety field ─────────────────────────────────────────────────

  _showVarietySection(initialQuery = "") {
    if (!this.hasVarietySectionTarget) return
    this.varietySectionTarget.classList.remove("hidden")
    this.varietyInputTarget.value = initialQuery
    this.commodityVarietyIdTarget.value = ""
    this._varietyResults = []
    this._varietyActiveIndex = -1
    this._fetchVarieties(initialQuery)
    this.varietyInputTarget.focus()
  }

  _hideVarietySection() {
    if (!this.hasVarietySectionTarget) return
    this.varietySectionTarget.classList.add("hidden")
    this._hideVarietyResults()
    this.commodityVarietyIdTarget.value = ""
  }

  _showAddVarietyLink() {
    if (!this.hasAddVarietyLinkTarget) return
    this.addVarietyLinkTarget.classList.remove("hidden")
  }

  _hideAddVarietyLink() {
    if (!this.hasAddVarietyLinkTarget) return
    this.addVarietyLinkTarget.classList.add("hidden")
  }

  openAddVariety(e) {
    e.preventDefault()
    this._openModal("")
  }

  async _onVarietyInput() {
    const q = this.varietyInputTarget.value.trim()
    this.commodityVarietyIdTarget.value = ""
    this._varietyActiveIndex = -1
    this._fetchVarieties(q)
  }

  _onVarietyKeydown(e) {
    const items = this.varietyResultsTarget.querySelectorAll("li")

    if (e.key === "ArrowDown") {
      e.preventDefault()
      this._setVarietyActive(Math.min(this._varietyActiveIndex + 1, items.length - 1), items)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      this._setVarietyActive(Math.max(this._varietyActiveIndex - 1, 0), items)
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (this._varietyResults[this._varietyActiveIndex]) {
        this._selectVariety(this._varietyResults[this._varietyActiveIndex])
      }
    } else if (e.key === "Escape") {
      this._hideVarietyResults()
    }
  }

  async _fetchVarieties(q) {
    if (!this._selectedCommodityId) return
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", q)
    url.searchParams.set("commodity_id", this._selectedCommodityId)
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    this._varietyResults = await response.json()
    this._renderVarietyResults(this._varietyResults, q)
  }

  _renderVarietyResults(results, q) {
    this.varietyResultsTarget.innerHTML = ""
    this._varietyActiveIndex = -1

    if (results.length === 0) {
      if (q && q.length > 0) {
        const li = document.createElement("li")
        li.classList.add("px-3", "py-2", "text-sm", "cursor-pointer", "hover:bg-base-200")
        li.dataset.openModal = "true"
        li.dataset.query = q
        li.innerHTML = `No varieties for <strong>${this._escape(q)}</strong> &mdash; <span class="link">add as a new variety</span>`
        li.addEventListener("mousedown", (e) => { e.preventDefault(); this._openModal(q) })
        this.varietyResultsTarget.appendChild(li)
        this.varietyResultsTarget.classList.remove("hidden")
      } else {
        this._hideVarietyResults()
      }
      return
    }

    results.forEach((result, index) => {
      const li = document.createElement("li")
      li.textContent = result.label
      li.classList.add("px-3", "py-2", "cursor-pointer", "hover:bg-base-200", "text-sm")
      li.addEventListener("mousedown", (e) => { e.preventDefault(); this._selectVariety(result) })
      li.addEventListener("mousemove", () => {
        const items = this.varietyResultsTarget.querySelectorAll("li")
        this._setVarietyActive(index, items)
      })
      this.varietyResultsTarget.appendChild(li)
    })

    this.varietyResultsTarget.classList.remove("hidden")
  }

  _selectVariety(result) {
    this.commodityVarietyIdTarget.value = result.commodity_variety_id
    this.varietyInputTarget.value = result.label
    this._varietyResults = []
    this._varietyActiveIndex = -1
    this._hideVarietyResults()
  }

  _setVarietyActive(index, items) {
    items.forEach(li => li.classList.remove("bg-base-200"))
    this._varietyActiveIndex = index
    const active = items[index]
    if (active) {
      active.classList.add("bg-base-200")
      active.scrollIntoView({ block: "nearest" })
    }
  }

  _hideVarietyResults() {
    if (!this.hasVarietyResultsTarget) return
    this.varietyResultsTarget.innerHTML = ""
    this.varietyResultsTarget.classList.add("hidden")
    this._varietyActiveIndex = -1
  }

  // ── Shared ─────────────────────────────────────────────────────────────────

  _setActive(index, items) {
    items.forEach(li => li.classList.remove("bg-base-200"))
    this._activeIndex = index
    const active = items[index]
    if (active) {
      active.classList.add("bg-base-200")
      active.scrollIntoView({ block: "nearest" })
    }
  }

  _hideResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
    this._activeIndex = -1
  }

  _onBlur(event) {
    if (!this.element.contains(event.target)) {
      this._hideResults()
      this._hideVarietyResults()
    }
  }

  _openModal(q) {
    this._hideResults()
    this._hideVarietyResults()
    if (!this.hasDialogTarget) return
    const frame = this.dialogTarget.querySelector("turbo-frame")
    if (frame) {
      const url = new URL(this.newUrlValue, window.location.origin)
      url.searchParams.set("name", q)
      url.searchParams.set("modal", "true")
      if (this._selectedCommodityId) url.searchParams.set("commodity_id", this._selectedCommodityId)
      frame.src = url.toString()
    }
    this.dialogTarget.showModal()
  }

  _onFrameLoad() {
    const frame = this.dialogTarget.querySelector("turbo-frame")
    if (!frame) return
    const payload = frame.querySelector("[data-variety-created]")
    if (!payload) return

    this.commodityIdTarget.value = payload.dataset.commodityId
    this.commodityVarietyIdTarget.value = payload.dataset.commodityVarietyId
    this.inputTarget.value = payload.dataset.label
    this.closeModal()
  }

  _escape(str) {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
